import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { usePermissions } from '../../context/PermissionContext.jsx';
import { useEmployees } from '../../context/EmployeeContext.jsx';
import apiClient from '../../utils/auth.js';
import { TOKEN } from '@constants/constants';
import { getCookie } from '../../utils/helpers.js';
import TitleIcon from '../../assets/title.png';

const Header = () => {
  const { user, logout } = useAuth();
  const { hasPermission, loading: permissionsLoading, userInfo } = usePermissions();
  const { refreshEmployees } = useEmployees();
  const location = useLocation();
  const navigate = useNavigate();

  // Employee ID from user_info (no separate /employees/me call needed)
  const employeeId = userInfo?.employee_id ?? null;
  
  // State management
  const [showProfileTooltip, setShowProfileTooltip] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [pendingUpdatesCount, setPendingUpdatesCount] = useState(0);
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  
  // Optimized: Single API call for pending updates count (only when tab is visible)
  const fetchPendingUpdatesCount = useCallback(async () => {
    if (document.hidden) return;
    try {
      const token = getCookie(TOKEN);
      if (!token) return;
      const response = await apiClient.get('/api/pending-updates/count');
      if (response.data?.count !== undefined) {
        setPendingUpdatesCount(response.data.count);
      }
    } catch (error) {
      console.error('Error fetching pending updates count:', error);
    }
  }, []);

  // Optimized: Single API call for pending updates list (only when tab is visible)
  const fetchPendingUpdates = useCallback(async () => {
    if (document.hidden) return;
    try {
      setLoadingNotifications(true);
      const response = await apiClient.get('/api/pending-updates?status=PENDING&limit=50');
      if (response.data?.pending_updates) {
        setPendingUpdates(response.data.pending_updates);
        if (response.data.total_count !== undefined) {
          setPendingUpdatesCount(response.data.total_count);
        }
      }
    } catch (error) {
      console.error('Error fetching pending updates:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  // Fetch pending updates count only when tab is visible: on mount, on visibility change (tab focused), and on route change
  useEffect(() => {
    if (permissionsLoading || !hasPermission('pending-updates-view')) return;
    if (document.hidden) return;
    fetchPendingUpdatesCount();

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      if (permissionsLoading || !hasPermission('pending-updates-view')) return;
      fetchPendingUpdatesCount();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [permissionsLoading, location.pathname, fetchPendingUpdatesCount, hasPermission]);

  // Fetch full list when dropdown opens (only when tab is visible)
  useEffect(() => {
    if (!showNotificationDropdown || permissionsLoading || !hasPermission('pending-updates-view')) return;
    if (document.hidden) return;
    fetchPendingUpdates();
    fetchPendingUpdatesCount();
  }, [showNotificationDropdown, permissionsLoading, hasPermission, fetchPendingUpdates, fetchPendingUpdatesCount]);

  const handleProfileClick = () => {
    if (employeeId) {
      navigate(`/employee-profile/${employeeId}`);
    }
  };

  const isActive = (path) => {
    if (path === '/kra') {
      return location.pathname === '/kra' || location.pathname.startsWith('/kra/');
    }
    if (path === '/culture') {
      return location.pathname === '/culture' || location.pathname.startsWith('/culture/');
    }
    if (path === '/employee-tree') {
      return location.pathname === '/employee-tree';
    }
    return location.pathname === path;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Handle approve/reject actions with confirmation
  const handleApprove = (updateId, e) => {
    e.stopPropagation();
    const update = pendingUpdates.find(u => u.id === updateId);
    const employeeName = update?.employee_name || update?.employee_email || 'this employee';
    const updateType = update?.update_type === 'CREATE' ? 'create' : 'update';
    
    setConfirmAction({ type: 'approve', updateId, employeeName, updateType });
    setShowConfirmModal(true);
  };

  const handleReject = (updateId, e) => {
    e.stopPropagation();
    const update = pendingUpdates.find(u => u.id === updateId);
    const employeeName = update?.employee_name || update?.employee_email || 'this employee';
    const updateType = update?.update_type === 'CREATE' ? 'create' : 'update';
    
    setConfirmAction({ type: 'reject', updateId, employeeName, updateType });
    setShowConfirmModal(true);
  };

  // Execute confirmed action - optimized to avoid redundant calls
  const executeConfirmedAction = async () => {
    if (!confirmAction || isProcessingAction) return;

    const { type, updateId } = confirmAction;
    setIsProcessingAction(true);
    
    try {
      const endpoint = type === 'approve' 
        ? `/api/pending-updates/${updateId}/approve`
        : `/api/pending-updates/${updateId}/reject`;
      
      const response = await apiClient.put(endpoint);
      
      if (response.data && (response.data.status === 'approved' || response.data.status === 'rejected')) {
        setShowConfirmModal(false);
        setConfirmAction(null);
        
        if (type === 'approve') {
          try {
            await refreshEmployees();
          } catch (refreshError) {
            console.error('Error refreshing employees:', refreshError);
          }
        }
        
        // Optimized: Fetch both in parallel
        await Promise.all([fetchPendingUpdates(), fetchPendingUpdatesCount()]);
      }
    } catch (error) {
      console.error(`Error ${type}ing update:`, error);
      alert(`Failed to ${type} update. Please try again.`);
      setShowConfirmModal(false);
      setConfirmAction(null);
    } finally {
      setIsProcessingAction(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <header className="bg-white backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="w-[97%] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-4">
              <Link 
                to="/workforce" 
                className="text-xl font-bold text-slate-800 cursor-pointer hover:opacity-80 transition-opacity duration-200"
              >
                <img src={TitleIcon} alt="SquareShift" className="h-10" />
              </Link>
            </div>
            
            {/* Navigation */}
            <nav className="flex gap-8">
              {(permissionsLoading || hasPermission('view-workforce')) && (
                <Link 
                  to="/workforce" 
                  className={`font-medium transition-colors ${
                    isActive('/workforce') 
                      ? 'text-slate-900 border-b-2 border-green-800 pb-1' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Workforce
                </Link>
              )}
              {(permissionsLoading || hasPermission('projects&allocations-view')) && (
                <Link 
                  to="/projects" 
                  className={`font-medium transition-colors ${
                    isActive('/projects') 
                      ? 'text-slate-900 border-b-2 border-green-800 pb-1' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Projects & Allocations
                </Link>
              )}
              {(permissionsLoading || hasPermission('skills-view')) && (
                <Link 
                  to="/skills" 
                  className={`font-medium transition-colors ${
                    isActive('/skills') 
                      ? 'text-slate-900 border-b-2 border-green-800 pb-1' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Skills
                </Link>
              )}
              {(permissionsLoading || hasPermission('kra-view')) && (
                <Link 
                  to="/kra" 
                  className={`font-medium transition-colors ${
                    isActive('/kra') 
                      ? 'text-slate-900 border-b-2 border-green-800 pb-1' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  KRA
                </Link>
              )}
              {(permissionsLoading || hasPermission('culture-performance-read')) && (
                <Link 
                  to="/culture" 
                  className={`font-medium transition-colors ${
                    isActive('/culture') 
                      ? 'text-slate-900 border-b-2 border-green-800 pb-1' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Culture
                </Link>
              )}
              {(permissionsLoading || hasPermission('view-employee-tree')) && (
                <Link 
                  to="/employee-tree" 
                  className={`font-medium transition-colors ${
                    isActive('/employee-tree') 
                      ? 'text-slate-900 border-b-2 border-green-800 pb-1' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Employee Tree
                </Link>
              )}
              {(permissionsLoading || hasPermission('audit-log-view')) && (
                <Link 
                  to="/audit-log" 
                  className={`font-medium transition-colors ${
                    isActive('/audit-log') 
                      ? 'text-slate-900 border-b-2 border-green-800 pb-1' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Audit Log
                </Link>
              )}
            </nav>
            
            {/* Right side - Notification Bell and User Menu */}
            <div className="flex items-center gap-4">
              {/* Notification Bell - Only show if user has permission */}
              {(permissionsLoading || hasPermission('pending-updates-view')) && (
                <div className="relative">
                  <button 
                    onClick={() => {
                      setShowNotificationDropdown(!showNotificationDropdown);
                    }}
                    className="text-slate-500 hover:text-slate-700 relative p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg transition-colors"
                    title={pendingUpdatesCount > 0 ? `${pendingUpdatesCount} pending employee update${pendingUpdatesCount > 1 ? 's' : ''}` : 'Employee updates notifications'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
                    </svg>
                    {pendingUpdatesCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {pendingUpdatesCount > 4 ? '4+' : pendingUpdatesCount}
                      </span>
                    )}
                  </button>
                  
                  {/* Notification Dropdown */}
                  {showNotificationDropdown && (
                    <div className="absolute right-0 mt-2 w-96 bg-white backdrop-blur-sm border border-slate-200 rounded-xl shadow-xl z-50 animate-fade-in">
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-900">
                              Employee Sync Queue
                            </h3>
                            {pendingUpdatesCount > 0 && (
                              <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-md flex items-center justify-center min-w-[1.5rem] h-6">
                                {pendingUpdatesCount}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => setShowNotificationDropdown(false)}
                            className="text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                        
                        {loadingNotifications ? (
                          <div className="flex items-center justify-center py-8 gap-2">
                            <div className="w-4 h-4 border-2 border-green-200 border-t-green-700 rounded-full animate-spin"></div>
                            <p className='text-sm'>Loading...</p>
                          </div>
                        ) : pendingUpdates.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            <svg className="w-8 h-8 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
                            </svg>
                            <p className="text-sm font-medium text-slate-600 mb-1">No notifications</p>
                            <p className="text-xs text-slate-400">All employee updates have been reviewed</p>
                          </div>
                        ) : (
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {pendingUpdates.map((update) => (
                              <div 
                                key={update.id} 
                                className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  update.update_type === 'CREATE' 
                                    ? 'bg-emerald-100' 
                                    : 'bg-blue-100'
                                }`}>
                                  {update.update_type === 'CREATE' ? (
                                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                      update.update_type === 'CREATE' 
                                        ? 'bg-emerald-100 text-emerald-700' 
                                        : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {update.update_type === 'CREATE' ? 'NEW' : 'UPDATE'}
                                    </span>
                                    <p className="text-sm text-slate-900 font-medium truncate">
                                      {update.employee_name || update.employee_email || 'Unknown Employee'}
                                    </p>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {update.employee_email && (
                                      <span className="block truncate">{update.employee_email}</span>
                                    )}
                                    <span className="block mt-1">{formatTimeAgo(update.created_at)}</span>
                                  </p>
                                  <div className="flex gap-2 mt-3">
                                    {update.update_type === 'UPDATE' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/pending-updates/${update.id}/compare`);
                                        }}
                                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                      >
                                        Compare
                                      </button>
                                    )}
                                    {update.update_type === 'CREATE' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/pending-updates/${update.id}/preview`);
                                        }}
                                        className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
                                      >
                                        Preview
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => handleApprove(update.id, e)}
                                      className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-green-700 hover:bg-green-800 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-700 focus:ring-offset-1"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={(e) => handleReject(update.id, e)}
                                      className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {pendingUpdatesCount > pendingUpdates.length && (
                              <div className="text-center pt-2 border-t border-slate-100">
                                <p className="text-xs text-slate-500">
                                  Showing {pendingUpdates.length} of {pendingUpdatesCount} updates
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* User Menu */}
              <div className="flex items-center gap-3">
                {/* Profile Account - Clickable */}
                <div 
                  className="relative flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 hover:bg-grey-700 hover:shadow-md"
                  onClick={handleProfileClick}
                  onMouseEnter={() => setShowProfileTooltip(true)}
                  onMouseLeave={() => setShowProfileTooltip(false)}
                  style={{ pointerEvents: employeeId ? 'auto' : 'none', opacity: employeeId ? 1 : 0.6 }}
                >
                  <div className={`avatar avatar-sm bg-[#426653] text-white-600`}>
                    <span>{user?.avatar || (user?.name ? user.name.charAt(0).toUpperCase() : 'A')}</span>
                  </div>
                  <span className="text-sm font-semibold">{user?.name || 'Admin User'}</span>
                  
                  {/* Tooltip */}
                  {showProfileTooltip && employeeId && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-lg shadow-lg whitespace-nowrap z-50 animate-fade-in">
                      View Profile
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -mb-1">
                        <div className="w-2 h-2 bg-slate-900 rotate-45"></div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Logout Button */}
                <button
                  onClick={logout}
                  className="text-slate-500 hover:text-slate-700 p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  title="Logout"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Click outside to close dropdowns */}
        {showNotificationDropdown && (
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => setShowNotificationDropdown(false)}
          ></div>
        )}
      </header>

      {/* Confirmation Modal - Outside header for proper centering */}
      {showConfirmModal && confirmAction && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          onClick={() => {
            if (!isProcessingAction) {
              setShowConfirmModal(false);
              setConfirmAction(null);
              setIsProcessingAction(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                confirmAction.type === 'approve' 
                  ? 'bg-green-100' 
                  : 'bg-red-100'
              }`}>
                {confirmAction.type === 'approve' ? (
                  <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900">
                  {confirmAction.type === 'approve' ? 'Approve Update' : 'Reject Update'}
                </h3>
              </div>
              <button
                onClick={() => {
                  if (!isProcessingAction) {
                    setShowConfirmModal(false);
                    setConfirmAction(null);
                    setIsProcessingAction(false);
                  }
                }}
                disabled={isProcessingAction}
                className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${
                  confirmAction.updateType === 'create' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {confirmAction.updateType === 'create' ? 'NEW EMPLOYEE' : 'EMPLOYEE UPDATE'}
                </span>
              </div>
              <p className="text-slate-700 mb-2">
                Are you sure you want to {confirmAction.type} {confirmAction.updateType === 'create' ? 'creating' : 'updating'}{' '}
                <span className="font-semibold text-slate-900">{confirmAction.employeeName}</span>?
              </p>
              <p className="text-sm text-slate-500">
                {confirmAction.updateType === 'create' 
                  ? 'This will create a new employee record in the system.'
                  : 'This will update the existing employee record in the system.'}
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  if (!isProcessingAction) {
                    setShowConfirmModal(false);
                    setConfirmAction(null);
                    setIsProcessingAction(false);
                  }
                }}
                disabled={isProcessingAction}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={executeConfirmedAction}
                disabled={isProcessingAction}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 flex items-center gap-2 justify-center min-w-[100px] ${
                  confirmAction.type === 'approve'
                    ? 'bg-green-700 hover:bg-green-800 focus:ring-green-700 disabled:bg-green-700 disabled:cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 focus:ring-red-500 disabled:bg-red-400 disabled:cursor-not-allowed'
                }`}
              >
                {isProcessingAction ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  confirmAction.type === 'approve' ? 'Approve' : 'Reject'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
